import { supabase } from "../config/supabase";
import { UserService } from "./UserService";
import {
  isReservedTenantSlug,
  isValidTenantSlug,
  normalizeTenantSlug,
} from "../utils/tenantSlug";

export type TenantUserRole = "admin" | "doctor" | "fisioterapeuta" | "patient";

export interface InitialTenantUserInput {
  userEmail: string;
  userDisplayName?: string;
  userRole: TenantUserRole;
  password?: string;
}

export interface TenantOverview {
  clientId: string;
  clientName: string;
  clientSlug: string;
  clientCreatedAt: string;
}

export interface TenantAdministrator {
  userId: string;
  userEmail: string;
  userDisplayName: string;
  userPhoneNumber?: string;
  current_client_id?: string | null;
  userRole: "admin";
}

export interface TenantDetail {
  tenant: TenantOverview;
  administrators: TenantAdministrator[];
}

export interface TenantAdminInput {
  userEmail: string;
  userDisplayName?: string;
  userPhoneNumber?: string;
  password?: string;
}

export interface TenantAdminUpdateInput {
  userDisplayName: string;
  userPhoneNumber?: string;
}

export interface TenantProvisioningResult {
  clientId: string;
  clientName: string;
  clientSlug?: string;
  assignments: Array<{
    email: string;
    role: TenantUserRole;
    action: "created" | "linked";
  }>;
}

type UserClientLinkRow = {
  userRole: string;
  users: {
    userId: string;
    userEmail: string;
    userDisplayName?: string | null;
    userPhoneNumber?: string | null;
    current_client_id?: string | null;
  } | null;
};

const normalizeEmail = (email: string) => email.trim().toLowerCase();
const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const mapAdministrator = (row: UserClientLinkRow): TenantAdministrator | null => {
  if (!row.users) {
    return null;
  }

  return {
    userId: row.users.userId,
    userEmail: row.users.userEmail,
    userDisplayName: row.users.userDisplayName?.trim() || row.users.userEmail,
    userPhoneNumber: row.users.userPhoneNumber ?? "",
    current_client_id: row.users.current_client_id ?? null,
    userRole: "admin",
  };
};

export class SuperAdminService {
  static async getTenantById(clientId: string): Promise<TenantOverview | null> {
    const { data, error } = await supabase
      .from("clients")
      .select("clientId, clientName, clientSlug, clientCreatedAt")
      .eq("clientId", clientId)
      .maybeSingle();

    if (error) {
      console.error("Error fetching tenant by id:", error);
      throw error;
    }

    return (data as TenantOverview | null) ?? null;
  }

  static async listTenants(): Promise<TenantOverview[]> {
    const { data, error } = await supabase
      .from("clients")
      .select("clientId, clientName, clientSlug, clientCreatedAt")
      .order("clientCreatedAt", { ascending: false });

    if (error) {
      console.error("Error fetching tenants:", error);
      throw error;
    }

    return (data ?? []) as TenantOverview[];
  }

  static async getClientBySlug(clientSlug: string): Promise<TenantOverview | null> {
    const normalizedSlug = normalizeTenantSlug(clientSlug);
    if (!normalizedSlug) {
      return null;
    }

    const { data, error } = await supabase.rpc("get_client_by_slug", {
      p_slug: normalizedSlug,
    });

    if (error) {
      console.error("Error resolving tenant by slug:", error);
      throw error;
    }

    const row = Array.isArray(data) ? data[0] : data;
    if (!row?.clientId) {
      return null;
    }

    return {
      clientId: row.clientId,
      clientName: row.clientName,
      clientSlug: row.clientSlug,
      clientCreatedAt: "",
    };
  }

  static async isClientSlugAvailable(clientSlug: string): Promise<boolean> {
    const normalizedSlug = normalizeTenantSlug(clientSlug);
    if (!normalizedSlug || !isValidTenantSlug(normalizedSlug) || isReservedTenantSlug(normalizedSlug)) {
      return false;
    }

    const existing = await this.getClientBySlug(normalizedSlug);
    return !existing;
  }

  static async listTenantAdministrators(clientId: string): Promise<TenantAdministrator[]> {
    const { data, error } = await supabase
      .from("user_clients")
      .select(`
        userRole,
        users (
          userId,
          userEmail,
          userDisplayName,
          userPhoneNumber,
          current_client_id
        )
      `)
      .eq("clientId", clientId)
      .eq("userRole", "admin");

    if (error) {
      console.error("Error fetching tenant administrators:", error);
      throw error;
    }

    return ((data ?? []) as UserClientLinkRow[])
      .map(mapAdministrator)
      .filter((administrator): administrator is TenantAdministrator => Boolean(administrator))
      .sort((left, right) => {
        const leftLabel = left.userDisplayName || left.userEmail;
        const rightLabel = right.userDisplayName || right.userEmail;
        return leftLabel.localeCompare(rightLabel, "pt-BR", { sensitivity: "base" });
      });
  }

  static async getTenantDetail(clientId: string): Promise<TenantDetail> {
    const [tenant, administrators] = await Promise.all([
      this.getTenantById(clientId),
      this.listTenantAdministrators(clientId),
    ]);

    if (!tenant) {
      throw new Error("Tenant não encontrado.");
    }

    return { tenant, administrators };
  }

  static async updateTenant(
    clientId: string,
    updates: { clientName: string; clientSlug: string }
  ): Promise<TenantOverview> {
    const currentTenant = await this.getTenantById(clientId);
    if (!currentTenant) {
      throw new Error("Tenant não encontrado.");
    }

    const normalizedClientName = updates.clientName.trim();
    if (!normalizedClientName) {
      throw new Error("Nome do tenant é obrigatório.");
    }

    const normalizedClientSlug = normalizeTenantSlug(updates.clientSlug || normalizedClientName);
    if (!normalizedClientSlug) {
      throw new Error("Slug do tenant é obrigatório.");
    }
    if (!isValidTenantSlug(normalizedClientSlug)) {
      throw new Error("Slug inválido: use apenas letras minúsculas, números e hífen.");
    }
    if (isReservedTenantSlug(normalizedClientSlug)) {
      throw new Error("Slug reservado. Escolha outro.");
    }

    if (normalizedClientSlug !== currentTenant.clientSlug) {
      const existingTenant = await this.getClientBySlug(normalizedClientSlug);
      if (existingTenant && existingTenant.clientId !== clientId) {
        throw new Error("Slug já está em uso. Escolha outro.");
      }
    }

    const { data, error } = await supabase
      .from("clients")
      .update({
        clientName: normalizedClientName,
        clientSlug: normalizedClientSlug,
      })
      .eq("clientId", clientId)
      .select("clientId, clientName, clientSlug, clientCreatedAt")
      .single();

    if (error || !data) {
      console.error("Error updating tenant:", error);
      if (error?.code === "23505") {
        throw new Error("Slug já está em uso. Escolha outro.");
      }
      throw error ?? new Error("Falha ao atualizar tenant.");
    }

    return data as TenantOverview;
  }

  static async addTenantAdministrator(
    clientId: string,
    input: TenantAdminInput
  ): Promise<{
    administrator: TenantAdministrator;
    action: "created" | "linked" | "promoted" | "already_admin";
  }> {
    const normalizedEmail = normalizeEmail(input.userEmail);
    if (!normalizedEmail) {
      throw new Error("Email do administrador é obrigatório.");
    }
    if (!emailRegex.test(normalizedEmail)) {
      throw new Error("Email do administrador é inválido.");
    }

    const trimmedDisplayName = input.userDisplayName?.trim();
    const trimmedPhoneNumber = input.userPhoneNumber?.trim();
    const trimmedPassword = input.password?.trim();
    const existingUser = await UserService.getUserByEmail(normalizedEmail);

    if (!existingUser) {
      if (!trimmedDisplayName) {
        throw new Error("Nome do administrador é obrigatório para novos usuários.");
      }
      if (!trimmedPassword) {
        throw new Error("Senha é obrigatória para criar um novo administrador.");
      }

      const { user } = await UserService.createUser(
        {
          userEmail: normalizedEmail,
          userDisplayName: trimmedDisplayName,
          userPhoneNumber: trimmedPhoneNumber,
        },
        "admin",
        clientId,
        trimmedPassword
      );

      if (!user) {
        throw new Error("Falha ao criar o administrador.");
      }

      await UserService.updateUser(user.userId, { current_client_id: clientId });

      return {
        administrator: {
          userId: user.userId,
          userEmail: user.userEmail,
          userDisplayName: user.userDisplayName || normalizedEmail,
          userPhoneNumber: user.userPhoneNumber ?? trimmedPhoneNumber ?? "",
          current_client_id: clientId,
          userRole: "admin",
        },
        action: "created",
      };
    }

    const { data: existingLink, error: linkError } = await supabase
      .from("user_clients")
      .select("userRole")
      .eq("userId", existingUser.userId)
      .eq("clientId", clientId)
      .maybeSingle();

    if (linkError) {
      console.error("Error fetching existing admin link:", linkError);
      throw linkError;
    }

    let action: "linked" | "promoted" | "already_admin" = "linked";

    if (!existingLink) {
      const { error: createLinkError } = await supabase.from("user_clients").insert({
        userId: existingUser.userId,
        clientId,
        userRole: "admin",
      });

      if (createLinkError) {
        console.error("Error linking admin to tenant:", createLinkError);
        if (createLinkError.code === "23505") {
          throw new Error("O usuário já está associado a este tenant.");
        }
        throw createLinkError;
      }
    } else if (existingLink.userRole !== "admin") {
      await UserService.updateUserRoleForClient(existingUser.userId, clientId, "admin");
      action = "promoted";
    } else {
      action = "already_admin";
    }

    if (!existingUser.current_client_id) {
      await UserService.updateUser(existingUser.userId, { current_client_id: clientId });
    }

    return {
      administrator: {
        userId: existingUser.userId,
        userEmail: existingUser.userEmail,
        userDisplayName: existingUser.userDisplayName || normalizedEmail,
        userPhoneNumber: existingUser.userPhoneNumber ?? "",
        current_client_id: existingUser.current_client_id ?? clientId,
        userRole: "admin",
      },
      action,
    };
  }

  static async updateTenantAdministrator(
    clientId: string,
    userId: string,
    updates: TenantAdminUpdateInput
  ): Promise<TenantAdministrator> {
    const { data: adminLink, error: adminLinkError } = await supabase
      .from("user_clients")
      .select("userRole")
      .eq("userId", userId)
      .eq("clientId", clientId)
      .maybeSingle();

    if (adminLinkError) {
      console.error("Error validating tenant administrator:", adminLinkError);
      throw adminLinkError;
    }

    if (!adminLink || adminLink.userRole !== "admin") {
      throw new Error("O usuário informado não é administrador deste tenant.");
    }

    const trimmedDisplayName = updates.userDisplayName.trim();
    if (!trimmedDisplayName) {
      throw new Error("Nome do administrador é obrigatório.");
    }

    const updatedUser = await UserService.updateUser(userId, {
      userDisplayName: trimmedDisplayName,
      userPhoneNumber: updates.userPhoneNumber?.trim() || "",
    });

    if (!updatedUser) {
      throw new Error("Falha ao atualizar o administrador.");
    }

    return {
      userId: updatedUser.userId,
      userEmail: updatedUser.userEmail,
      userDisplayName: updatedUser.userDisplayName || updatedUser.userEmail,
      userPhoneNumber: updatedUser.userPhoneNumber ?? "",
      current_client_id: updatedUser.current_client_id ?? null,
      userRole: "admin",
    };
  }

  static async removeTenantAdministrator(clientId: string, userId: string): Promise<void> {
    const [{ count, error: countError }, { data: currentLink, error: linkError }] = await Promise.all([
      supabase
        .from("user_clients")
        .select("userId", { count: "exact", head: true })
        .eq("clientId", clientId)
        .eq("userRole", "admin"),
      supabase
        .from("user_clients")
        .select("userRole")
        .eq("userId", userId)
        .eq("clientId", clientId)
        .maybeSingle(),
    ]);

    if (countError) {
      console.error("Error counting tenant administrators:", countError);
      throw countError;
    }

    if (linkError) {
      console.error("Error validating tenant administrator removal:", linkError);
      throw linkError;
    }

    if (!currentLink || currentLink.userRole !== "admin") {
      throw new Error("O usuário informado não é administrador deste tenant.");
    }

    if ((count ?? 0) <= 1) {
      throw new Error("O tenant precisa manter ao menos um administrador.");
    }

    await UserService.deleteUserFromClient(userId, clientId);

    const { data: userRecord, error: userRecordError } = await supabase
      .from("users")
      .select("current_client_id")
      .eq("userId", userId)
      .maybeSingle();

    if (userRecordError) {
      console.error("Error fetching removed administrator state:", userRecordError);
      throw userRecordError;
    }

    if (userRecord?.current_client_id === clientId) {
      const { data: remainingLinks, error: remainingLinksError } = await supabase
        .from("user_clients")
        .select("clientId")
        .eq("userId", userId)
        .order("createdAt", { ascending: true })
        .limit(1);

      if (remainingLinksError) {
        console.error("Error fetching remaining tenant links:", remainingLinksError);
        throw remainingLinksError;
      }

      const nextClientId = (remainingLinks as Array<{ clientId: string }> | null)?.[0]?.clientId ?? null;
      await UserService.updateUser(userId, { current_client_id: nextClientId });
    }
  }

  static async createTenantWithInitialUsers(
    clientName: string,
    rawAssignments: InitialTenantUserInput[],
    clientSlugInput?: string
  ): Promise<TenantProvisioningResult> {
    const normalizedClientName = clientName.trim();
    if (!normalizedClientName) {
      throw new Error("Nome do tenant é obrigatório.");
    }

    const normalizedClientSlug = normalizeTenantSlug(clientSlugInput || normalizedClientName);
    if (!normalizedClientSlug) {
      throw new Error("Slug do tenant é obrigatório.");
    }
    if (!isValidTenantSlug(normalizedClientSlug)) {
      throw new Error("Slug inválido: use apenas letras minúsculas, números e hífen.");
    }
    if (isReservedTenantSlug(normalizedClientSlug)) {
      throw new Error("Slug reservado. Escolha outro.");
    }

    const assignments = rawAssignments
      .map((assignment) => ({
        ...assignment,
        userEmail: normalizeEmail(assignment.userEmail),
        userDisplayName: assignment.userDisplayName?.trim(),
        password: assignment.password?.trim(),
      }))
      .filter((assignment) => assignment.userEmail);

    if (assignments.length === 0) {
      throw new Error("Informe ao menos um usuário inicial.");
    }

    const nonAdminAssignment = assignments.find((assignment) => assignment.userRole !== "admin");
    if (nonAdminAssignment) {
      throw new Error(
        `O superAdmin pode provisionar apenas usuários admin. Papel inválido para ${nonAdminAssignment.userEmail}.`
      );
    }

    const emailSet = new Set<string>();
    for (const assignment of assignments) {
      if (emailSet.has(assignment.userEmail)) {
        throw new Error(`Email duplicado na lista de usuários: ${assignment.userEmail}`);
      }
      emailSet.add(assignment.userEmail);
    }

    const existingUsersByEmail = new Map<string, Awaited<ReturnType<typeof UserService.getUserByEmail>>>();
    for (const assignment of assignments) {
      const existingUser = await UserService.getUserByEmail(assignment.userEmail);
      existingUsersByEmail.set(assignment.userEmail, existingUser);

      if (!existingUser) {
        if (!assignment.userDisplayName || assignment.userDisplayName.trim().length === 0) {
          throw new Error(
            `Usuário ${assignment.userEmail} não existe. O nome do usuário é obrigatório para novos cadastros.`
          );
        }

        if (!assignment.password || assignment.password.trim().length === 0) {
          throw new Error(
            `Usuário ${assignment.userEmail} não existe. Informe uma senha para criar a conta.`
          );
        }
      }
    }

    const slugIsAvailable = await this.isClientSlugAvailable(normalizedClientSlug);
    if (!slugIsAvailable) {
      throw new Error("Slug já está em uso. Escolha outro.");
    }

    const { data: newClient, error: createClientError } = await supabase
      .from("clients")
      .insert({ clientName: normalizedClientName, clientSlug: normalizedClientSlug })
      .select("clientId, clientName, clientSlug")
      .single();

    if (createClientError || !newClient) {
      console.error("Error creating tenant:", createClientError);
      if (createClientError?.code === "23505") {
        throw new Error("Slug já está em uso. Escolha outro.");
      }
      throw createClientError ?? new Error("Falha ao criar tenant.");
    }

    const result: TenantProvisioningResult = {
      clientId: newClient.clientId,
      clientName: newClient.clientName,
      clientSlug: newClient.clientSlug,
      assignments: [],
    };

    for (const assignment of assignments) {
      const existingUser = existingUsersByEmail.get(assignment.userEmail) ?? null;
      const generatedName =
        existingUser?.userDisplayName
          ? existingUser.userDisplayName
          : assignment.userDisplayName && assignment.userDisplayName.length > 0
            ? assignment.userDisplayName
            : assignment.userEmail.split("@")[0];

      const { user, isNew } = await UserService.createUser(
        {
          userEmail: assignment.userEmail,
          userDisplayName: generatedName,
        },
        assignment.userRole,
        newClient.clientId,
        assignment.password
      );

      if (!user) {
        throw new Error(`Falha ao provisionar usuário ${assignment.userEmail}.`);
      }

      if (assignment.userRole === "admin") {
        await UserService.updateUser(user.userId, { current_client_id: newClient.clientId });
      }

      result.assignments.push({
        email: assignment.userEmail,
        role: assignment.userRole,
        action: isNew ? "created" : "linked",
      });
    }

    return result;
  }
}
